'use client';

import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import {
  BookOpen,
  ArrowRight,
  TrendUp,
  Clock,
  CloudLightning,
  ChartLineUp,
  Warning,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { SEOHead } from '@/components/seo/SEOHead';
import { AnimatedSection } from '@/components/marketing/AnimatedSection';

interface CaseStudy {
  id: string;
  company: string;
  companyType: string;
  location: string;
  revenue: string;
  image: string;
  imageAlt: string;
  icon: typeof TrendUp;
  title: string;
  challenge: string;
  solution: string;
  results: { value: string; label: string }[];
}

const caseStudies: CaseStudy[] = [
  {
    id: 'premier-residential',
    company: 'Premier Exteriors',
    companyType: 'Residential roofing contractor',
    location: 'Chicago, IL',
    revenue: '$2.5M',
    image:
      'https://images.unsplash.com/photo-1632778149955-e80f8ce6a1f5?q=80&w=1600&auto=format&fit=crop',
    imageAlt: 'Completed asphalt shingle roof on a suburban home',
    icon: Clock,
    title: 'From 4-hour lead response to 15 minutes',
    challenge:
      'Leads were landing in a shared inbox and getting claimed by whoever saw them first. Response times stretched past four hours, and warm prospects cooled off before a rep could call.',
    solution:
      'Rooflyzer routed website widget leads directly into the CRM with property data, auto-assigned reps by zip code, and triggered immediate SMS and email follow-up sequences.',
    results: [
      { value: '15 min', label: 'Average lead response' },
      { value: '+72%', label: 'Close rate improvement' },
      { value: '$250/mo', label: 'Software savings' },
    ],
  },
  {
    id: 'atlas-commercial',
    company: 'Atlas Commercial Roofing',
    companyType: 'Commercial roofing contractor',
    location: 'Dallas, TX',
    revenue: '$8M',
    image:
      'https://images.unsplash.com/photo-1504307651254-35680f356dfd?q=80&w=1600&auto=format&fit=crop',
    imageAlt: 'Commercial flat roof installation with crew and materials',
    icon: TrendUp,
    title: 'Consolidating 5 tools into one operating system',
    challenge:
      'The team ran CRM, estimating, project management, invoicing, and marketing in separate apps. Job profitability was calculated quarterly, and AR aging was only visible in QuickBooks.',
    solution:
      'Rooflyzer replaced the disconnected stack with a single pipeline, real-time job costing, and QuickBooks sync. Project managers now see labor, materials, and invoices in one view.',
    results: [
      { value: '45 → 22', label: 'Days in accounts receivable' },
      { value: 'Real-time', label: 'Job profitability visibility' },
      { value: '-60%', label: 'Monthly software spend' },
    ],
  },
  {
    id: 'highland-storm',
    company: 'Highland Storm Restoration',
    companyType: 'Storm restoration contractor',
    location: 'Denver, CO',
    revenue: '$1.2M',
    image:
      'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?q=80&w=1600&auto=format&fit=crop',
    imageAlt: 'Aerial view of suburban rooftops after a hail storm',
    icon: CloudLightning,
    title: 'Three hail events, 200 qualified leads, 42 jobs closed',
    challenge:
      'Storm chasing meant guesswork. Reps drove neighborhoods randomly, wasting hours on undamaged properties and missing streets with the highest hail impact.',
    solution:
      'Rooflyzer storm intelligence mapped hail paths within 24 hours. The team built territory lists, planned canvassing routes, and pushed leads straight into the sales pipeline.',
    results: [
      { value: '200', label: 'Qualified leads in 2 weeks' },
      { value: '42', label: 'Jobs closed at $12k avg' },
      { value: '+50%', label: 'Canvassing efficiency' },
    ],
  },
  {
    id: 'roofsource-agency',
    company: 'RoofSource Marketing',
    companyType: 'Roofing marketing agency',
    location: 'Atlanta, GA',
    revenue: '50 contractor clients',
    image:
      'https://images.unsplash.com/photo-1551434678-e076c223a692?q=80&w=1600&auto=format&fit=crop',
    imageAlt: 'Marketing team reviewing campaign analytics on a screen',
    icon: ChartLineUp,
    title: 'Proving 3.5x ROI and keeping clients longer',
    challenge:
      'The agency could not close the loop between ad spend and signed contracts. Clients churned when they could not see which campaigns were generating revenue.',
    solution:
      'Rooflyzer connected Google Ads, call tracking, and CRM attribution to actual revenue. The agency built white-label client dashboards showing cost per lead, cost per job, and ROI.',
    results: [
      { value: '3.5x', label: 'ROI on Google Ads spend' },
      { value: '+40%', label: 'Client retention' },
      { value: '50', label: 'Contractor dashboards live' },
    ],
  },
];

function CaseStudyCard({ study, index }: { study: CaseStudy; index: number }) {
  const reduced = useReducedMotion();
  const Icon = study.icon;
  const isReversed = index % 2 === 1;

  return (
    <motion.article
      initial={reduced ? false : { opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="rounded-3xl overflow-hidden bg-white/[0.03] border border-white/[0.08]"
    >
      <div className={`grid lg:grid-cols-2 ${isReversed ? 'lg:grid-flow-col-dense' : ''}`}>
        <div className={`relative h-64 lg:h-auto ${isReversed ? 'lg:col-start-2' : ''}`}>
          <img
            src={study.image}
            alt={study.imageAlt}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050507] via-[#050507]/50 to-transparent" />
          <div className="absolute bottom-5 left-5 right-5">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-9 h-9 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center">
                <Icon className="w-5 h-5 text-emerald-400" weight="bold" />
              </div>
              <span className="text-xs font-mono font-bold uppercase tracking-wider text-emerald-400">
                {study.companyType}
              </span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold text-white leading-tight">
              {study.title}
            </h3>
          </div>
        </div>

        <div className={`p-8 sm:p-10 flex flex-col ${isReversed ? 'lg:col-start-1' : ''}`}>
          <div className="flex flex-wrap items-center gap-3 text-xs font-semibold uppercase tracking-wider text-white/45 mb-6">
            <span>{study.company}</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>{study.location}</span>
            <span className="w-1 h-1 rounded-full bg-white/30" />
            <span>{study.revenue}</span>
          </div>

          <div className="space-y-6 mb-8 flex-grow">
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-white/55 mb-2">
                Challenge
              </h4>
              <p className="text-white/70 leading-relaxed">{study.challenge}</p>
            </div>
            <div>
              <h4 className="text-sm font-bold uppercase tracking-wider text-white/55 mb-2">
                Solution
              </h4>
              <p className="text-white/70 leading-relaxed">{study.solution}</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 p-5 rounded-2xl bg-white/[0.03] border border-white/[0.08] mb-8">
            {study.results.map((result, i) => (
              <div key={i} className="text-center">
                <div className="text-2xl font-mono font-bold text-white mb-1">{result.value}</div>
                <div className="text-[0.65rem] uppercase tracking-wider text-white/45 font-semibold leading-tight">
                  {result.label}
                </div>
              </div>
            ))}
          </div>

          <Button
            variant="outline"
            size="sm"
            className="w-full sm:w-auto self-start rounded-lg border border-slate-700 text-slate-200 hover:bg-slate-800/60 hover:text-white"
            asChild
          >
            <Link to="/demo">
              See how it works
              <ArrowRight className="ml-2 size-4" />
            </Link>
          </Button>
        </div>
      </div>
    </motion.article>
  );
}

export function CaseStudiesPage() {
  const reduced = useReducedMotion();

  return (
    <>
      <SEOHead
        title="Rooflyzer Case Studies | Real Results for Roofing Businesses"
        description="See how residential, commercial, and storm restoration contractors use Rooflyzer OS to grow revenue, close more jobs, and streamline operations."
        canonicalUrl="/customers/case-studies"
        keywords="roofing software case studies, roofing CRM success stories, roofing contractor ROI, roofing business growth"
        schema={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://rooflyzer.pro' },
            {
              '@type': 'ListItem',
              position: 2,
              name: 'Customers',
              item: 'https://rooflyzer.pro/customers',
            },
            {
              '@type': 'ListItem',
              position: 3,
              name: 'Case Studies',
              item: 'https://rooflyzer.pro/customers/case-studies',
            },
          ],
        }}
      />

      <section className="relative pt-28 pb-16 px-4 sm:px-6 text-center overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto">
          <AnimatedSection>
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-700 bg-slate-800/80 text-slate-300 text-xs font-mono font-medium uppercase tracking-wider mb-6">
              <BookOpen className="w-4 h-4 text-emerald-400" weight="fill" />
              <span>Customer Success Stories</span>
            </div>
          </AnimatedSection>

          <AnimatedSection delay={0.1}>
            <h1 className="hero-h1 font-bold text-white mb-5">
              Real Results for <span className="text-white">Real Roofers.</span>
            </h1>
          </AnimatedSection>

          <AnimatedSection delay={0.2}>
            <p className="hero-lead text-white/65 max-w-2xl mx-auto mb-8">
              These are illustrative examples based on common outcomes we see with contractors using
              Rooflyzer OS.
            </p>
          </AnimatedSection>

          <AnimatedSection delay={0.3}>
            <div className="inline-flex items-center gap-2 px-3 py-2 rounded-2xl bg-white/[0.03] border border-white/[0.08]">
              <Warning className="w-4 h-4 text-emerald-400" />
              <span className="text-xs text-white/60">
                Results vary by business size, market, and execution.
              </span>
            </div>
          </AnimatedSection>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto space-y-12">
          {caseStudies.map((study, i) => (
            <CaseStudyCard key={study.id} study={study} index={i} />
          ))}
        </div>
      </section>

      <section className="py-20 px-4 sm:px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={reduced ? false : { opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="rounded-3xl bg-white/[0.03] border border-white/[0.08] p-8 md:p-12 text-center space-y-6"
          >
            <h2 className="section-h2 font-bold text-white">Want results like these?</h2>
            <p className="body-lg text-white/60 max-w-xl mx-auto">
              Get a personalized walkthrough of the features that match your business model.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Button
                size="lg"
                className="rounded-lg bg-white text-slate-950 hover:bg-slate-100 font-semibold px-8 shadow-sm"
                asChild
              >
                <Link to="/demo">
                  Book a demo
                  <ArrowRight className="ml-2 size-5" />
                </Link>
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-lg border border-slate-700 text-slate-200 hover:text-white hover:bg-slate-800/60 px-8"
                asChild
              >
                <Link to="/contact">Contact sales</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </>
  );
}
